import { Controller } from "@hotwired/stimulus"

// Manages page layout width between wide (full width) and read (constrained) modes
// Usage:
//   data-controller="layout-width" on body
//   data-action="click->layout-width#toggle" on toggle button
//   data-action="change->layout-width#select" on radio inputs
export default class extends Controller {
  static targets = ["icon", "label", "wideRadio", "readRadio"]
  static values = {
    current: { type: String, default: "wide" }
  }

  connect() {
    const savedWidth = localStorage.getItem("layout_width") || "wide"
    this.applyLayoutWidth(savedWidth, false)
    this.updateLabels()
  }

  select(event) {
    this.applyLayoutWidth(event.target.value, true)
  }

  toggle() {
    const newWidth = this.currentValue === "wide" ? "read" : "wide"
    this.applyLayoutWidth(newWidth, true)
  }

  applyLayoutWidth(width, animate = true) {
    this.currentValue = width

    if (animate) {
      document.documentElement.classList.add("layout-width-transitioning")
      setTimeout(() => {
        document.documentElement.classList.remove("layout-width-transitioning")
      }, 300)
    }

    document.documentElement.dataset.layoutWidth = width
    document.body.dataset.layoutWidth = width

    this.updateIcons()
    this.updateLabels()
    this.updateRadios()
    this.updateLayoutCards()

    localStorage.setItem("layout_width", width)
    this.syncToServer(width)
  }

  updateIcons() {
    if (!this.hasIconTarget) return

    const isRead = this.currentValue === "read"
    this.iconTargets.forEach((icon) => {
      if (icon.classList.contains("bi")) {
        icon.className = isRead ? "bi bi-book" : "bi bi-arrows-angle-expand"
      } else if (icon.classList.contains("fas")) {
        icon.className = isRead ? "fas fa-book-open" : "fas fa-expand-arrows-alt"
      }
    })
  }

  updateLabels() {
    if (!this.hasLabelTarget) return

    const labelKey = this.currentValue === "read" ? "read" : "wide"
    const labelText = this.element.dataset[`layoutWidth${labelKey.charAt(0).toUpperCase() + labelKey.slice(1)}Label`]
      || document.body.dataset[`layoutWidth${labelKey.charAt(0).toUpperCase() + labelKey.slice(1)}Label`]

    if (labelText) {
      this.labelTargets.forEach((label) => {
        label.textContent = labelText
      })
    }
  }

  updateRadios() {
    if (this.hasWideRadioTarget) {
      this.wideRadioTarget.checked = this.currentValue === "wide"
    }
    if (this.hasReadRadioTarget) {
      this.readRadioTarget.checked = this.currentValue === "read"
    }
  }

  updateLayoutCards() {
    document.querySelectorAll("[data-layout-card]").forEach((card) => {
      if (card.dataset.layoutCard === this.currentValue) {
        card.classList.add("active")
      } else {
        card.classList.remove("active")
      }
    })
  }

  async syncToServer(width) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) return

    try {
      await fetch("/api/v1/preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ key: "layout_width", value: width })
      })
    } catch (error) {
      console.debug("Could not sync layout width preference to server:", error)
    }
  }
}
